// audio-player.ts — Vesperas einheitlicher, gestylter Audio-Player
// (.player-bar mit Play/Pause, Seek, Zeit). 2026-07-13 aus alltag-view.ts
// herausgezogen, damit auch die MP3-Karte im Chat (tool-cards.ts) denselben
// schönen Player nutzt statt des grauen Browser-Standard-Players.

import { hookHiFiPlayback, prewarmHiFi } from "./playback-hifi.js";
import { betritt, type Buehnenplatz } from "./ton-leitstand.js";

const PLAY_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
const PAUSE_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="currentColor"><path d="M6 19h4V5H6zm8-14v14h4V5z"/></svg>';
const STOP_ICON =
  '<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" fill="currentColor"><path d="M6 6h12v12H6z"/></svg>';

/** Formatiert Sekunden als m:ss. */
export function formatClock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

/**
 * Dauer aus recordedAt/recordedUntil (ffprobe der gemergten Chunks beim
 * Abschluss). 0 wenn unbekannt — dann darf der Player nicht 0:00/0:00 lügen.
 */
export function durationFromRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): number {
  if (!startIso || !endIso) return 0;
  const a = Date.parse(startIso);
  const b = Date.parse(endIso);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  const sec = (b - a) / 1000;
  return Number.isFinite(sec) && sec > 0 ? sec : 0;
}

/**
 * HTMLMedia.duration ist auf iOS bei abgerissenen/unvollständigen m4a oft
 * 0, NaN oder Infinity. Dann seekable-Ende oder die bekannte Chunk-Dauer.
 */
export function resolveMediaDuration(
  mediaDuration: number,
  seekableEnd: number,
  knownSec = 0,
): number {
  if (Number.isFinite(mediaDuration) && mediaDuration > 0) return mediaDuration;
  if (Number.isFinite(seekableEnd) && seekableEnd > 0) return seekableEnd;
  if (Number.isFinite(knownSec) && knownSec > 0) return knownSec;
  return 0;
}

/**
 * Player-Zeittext. Nie "0:00 / 0:00": echte Dauer, sonst ehrlich
 * "abgerissen · abspielbar" (und währenddessen die laufende Position).
 */
export function playerTimeLabel(
  currentSec: number,
  durationSec: number,
  incomplete = false,
): string {
  const cur = formatClock(currentSec);
  if (durationSec > 0) return `${cur} / ${formatClock(durationSec)}`;
  if (incomplete) {
    return currentSec > 0.5 ? `${cur} / abgerissen` : "abgerissen · abspielbar";
  }
  return `${cur} / –`;
}

/** Zeichen fuer Herz und Sichern — im Stil der uebrigen Leisten-Knoepfe. */
const HERZ_LEER =
  '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"><path d="M12 20s-7-4.6-7-9.3A4 4 0 0 1 12 8a4 4 0 0 1 7 2.7C19 15.4 12 20 12 20Z"/></svg>';
const HERZ_VOLL =
  '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" fill="currentColor"><path d="M12 20s-7-4.6-7-9.3A4 4 0 0 1 12 8a4 4 0 0 1 7 2.7C19 15.4 12 20 12 20Z"/></svg>';
const SICHERN_ICON =
  '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v11"/><path d="m8 11 4 4 4-4"/><path d="M5 19h14"/></svg>';

type AudioPlayerOpts = {
  /** Bekannte Dauer in Sekunden (Datei/Chunks, recordedUntil − recordedAt). */
  durationSec?: number;
  /** Unvollständig/abgerissen — Label nicht 0:00/0:00, trotzdem Play. */
  incomplete?: boolean;
  /**
   * Was hier klingt, in Martins Worten — für die Abspielleiste.
   * Fehlt der Titel, steht dort schlicht „Aufnahme".
   */
  titel?: string;
  /**
   * Nur der Startknopf, keine eigene Leiste.
   *
   * 2026-09-04 (Martin): *„Wenn wir nur einen Player hätten auf der
   * jeweiligen Seite und einen Schnittpunkt zu dem Player. Wenn ich im
   * Alltag eine Sequenz öffne, dass dort ein Button ist."*
   *
   * Zehn aufgeklappte Einträge hießen zehn Leisten mit zehn Uhren — und
   * keine sagte, welche gerade läuft. Der Knopf startet; alles Weitere
   * (Titel, Zeit, Anhalten, Springen) zeigt die eine Abspielleiste.
   *
   * ⚠️ Es bleibt DERSELBE Player mit derselben Maschinerie — nur die
   * Bedienteile, die es jetzt einmal zentral gibt, sind verborgen. Eine
   * zweite, schlankere Player-Sorte wäre der Anfang der nächsten
   * Uneinigkeit.
   */
  kompakt?: boolean;
  /**
   * Ein Herz und ein Teilen-Knopf in der Leiste. (07.09.2026)
   *
   * Martin, nach dem ersten wirklich gelungenen Klang des Tages: *„Ich würde
   * gerne den Player erweitern mit einem Download-Button und einem
   * Herz-Button als Favorit, damit ich die Lieder auch zusätzlich speichern
   * und abspielen kann."*
   *
   * ⚠️ Beides ist OPTIONAL und fehlt überall dort, wo es nichts zu merken
   *    gibt — eine Alltag-Aufnahme braucht kein Herz. Ein Player, der
   *    überall alles zeigt, ist der Anfang der nächsten Unordnung.
   */
  merken?: {
    /** Steht das Stück schon auf der Liste? */
    an: boolean;
    /** Umschalten. Wirft nie — der Aufrufer meldet selbst, was schiefging. */
    umschalten: (an: boolean) => Promise<boolean>;
  };
  /**
   * Zum Speichern anbieten.
   *
   * ⚠️ Auf dem iPhone gibt es keinen Download-Ordner, und `<a download>` tut
   *    in der WKWebView nichts. Der Weg, der dort wirklich speichert, ist das
   *    Teilen-Blatt mit der Datei daran — „In Dateien sichern" IST der
   *    Download. Deshalb heisst der Knopf auch nicht „Herunterladen".
   */
  sichern?: { dateiname: string };
};

/**
 * Styled Audio-Player (gleiche .player-bar-Klassen wie buildPlayerBar in app.ts).
 * `source` = direkte URL (Aufnahme-/MP3-Files) ODER ein lazy-Resolver, der beim
 * ersten Play eine URL liefert (z.B. TTS-Vorbereitung).
 */
/*
 * 2026-09-04: Hier stand ein modulweiter Ring (`currentlyPlaying`), der nur
 * Player gegen Player kannte. Ein zweiter derselben Art lag in
 * narration-engine.ts (`activeNarrationEngine`) — und die beiden wussten
 * nichts voneinander.
 *
 * Genau daher Martins Beobachtung: *„Man hört zwar eine Stimme, aber der
 * Player läuft offensichtlich nicht."* Kein Fehler, den man findet — beide
 * Seiten arbeiteten korrekt, nur aneinander vorbei.
 *
 * Beide Ringe sind jetzt EINER: ton-leitstand.ts.
 */

function seekableEndOf(audio: HTMLAudioElement): number {
  try {
    if (audio.seekable.length > 0) {
      return audio.seekable.end(audio.seekable.length - 1);
    }
  } catch {
    /* seekable nicht lesbar */
  }
  return 0;
}

export function buildAudioPlayer(
  source: string | (() => Promise<string>),
  opts?: AudioPlayerOpts,
): HTMLElement {
  const knownSec =
    typeof opts?.durationSec === "number" &&
    Number.isFinite(opts.durationSec) &&
    opts.durationSec > 0
      ? opts.durationSec
      : 0;
  const incomplete = opts?.incomplete === true;

  const wrap = document.createElement("div");
  wrap.className = "alltag-player";
  if (opts?.kompakt) wrap.classList.add("alltag-player--kompakt");

  const audio = new Audio();
  // 2026-07-15 (Martin): waehrend echter Wiedergabe auf hochwertige A2DP-
  // Wiedergabe schalten (kein blechernes HFP-Telefonprofil ueber Bluetooth).
  // Geteilte Logik mit Vorlesen/TTS und Bot-Stimme -> playback-hifi.ts.
  hookHiFiPlayback(audio);
  // 2026-07-14 (Martin): nur EIN Ton beansprucht die Bühne. Seit dem
  // 04.09. gilt das über ALLE Wege hinweg — auch gegen das Vorlesen.
  let platz: Buehnenplatz | null = null;
  audio.addEventListener("play", () => {
    platz = betritt({
      art: "spur",
      titel: opts?.titel?.trim() || "Aufnahme",
      anhalten: () => audio.pause(),
      // Stopp gibt die Stelle auf — sonst spielt die Spur beim naechsten
      // Antippen mittendrin weiter, obwohl der Player geschlossen war.
      beenden: () => {
        audio.pause();
        try {
          audio.currentTime = 0;
        } catch {
          /* manche Quellen erlauben kein Setzen — dann eben nur Pause */
        }
      },
      fortsetzen: () => void audio.play().catch(() => {}),
      abspielzeit: () => audio.currentTime,
      fortschritt: () => ({
        position: audio.currentTime,
        dauer: effectiveDuration(),
      }),
      springe: (sek) => {
        const ziel = audio.currentTime + sek;
        const ende = effectiveDuration();
        audio.currentTime = Math.max(0, ende > 0 ? Math.min(ende, ziel) : ziel);
      },
    });
  });
  audio.addEventListener("pause", () => platz?.melde(false));
  audio.addEventListener("ended", () => {
    platz?.verlasse();
    platz = null;
  });
  audio.preload = "metadata";
  if (typeof source === "string") audio.src = source;
  let resolving = false;

  const bar = document.createElement("div");
  bar.className = "player-bar";

  const playBtn = document.createElement("button");
  playBtn.type = "button";
  playBtn.className = "player-bar-play";

  /**
   * Zustand des Vorlese-Knopfs — EINE Stelle für Zeichen, Wort und Ansage.
   *
   * 2026-08-25: Vorher stand an FÜNF Stellen dasselbe `innerHTML = ...`, und
   * keine davon setzte ein aria-label — die sichtbare Beschriftung war die
   * einzige Ansage. Auf dem Telefon blendet die Leiste sie jetzt aus (sonst
   * bricht sie in drei Reihen um), und ohne aria-label wäre der Knopf für
   * einen Screenreader stumm gewesen.
   *
   * `data-state` erlaubt der Leiste ausserdem, den Fehlerfall anders zu
   * behandeln: dort bleibt das Wort stehen, weil das Zeichen (ein Dreieck)
   * nicht sagt, dass etwas schiefging.
   */
  /*
   * ⚠️ 23.09.2026 — der kompakte Knopf trägt die DAUER. Martin: *„Der
   *    Player passt nicht zum Erscheinungsbild, der schaut eigenartig aus
   *    mit diesem Wiedergabefeld."* Ohne Uhr und Schieber (die stehen seit
   *    dem 04.09. einmal zentral in der Abspielleiste) war der Knopf ein
   *    Kreis in einer leeren Fläche und sagte nicht, wie lang die Aufnahme
   *    ist. Jetzt: „Abspielen · 0:25".
   */
  const kompakt = opts?.kompakt === true;
  const mitDauer = (wort: string, zustand: string): string => {
    if (!kompakt || zustand !== "bereit") return wort;
    const d =
      knownSec > 0
        ? knownSec
        : Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : 0;
    return d > 0 ? `${wort} · ${formatClock(d)}` : wort;
  };
  const setzeKnopf = (icon: string, grundwort: string, zustand: string): void => {
    // Die Ansage folgt dem SICHTBAREN Wort — samt Dauer, wo es sie trägt.
    const wort = mitDauer(grundwort, zustand);
    playBtn.innerHTML =
      `${icon}<span class="player-bar-play-label">${wort}</span>`;
    playBtn.dataset.state = zustand;
    playBtn.setAttribute("aria-label", wort);
    playBtn.title = wort;
  };
  setzeKnopf(PLAY_ICON, "Abspielen", "bereit");

  // 2026-07-14 (Martin, "Pause-Taste, aber keine Stopptaste?"): Stopp =
  // anhalten UND an den Anfang zuruecksetzen.
  const stopBtn = document.createElement("button");
  stopBtn.type = "button";
  stopBtn.className = "player-bar-skip player-bar-stop";
  stopBtn.setAttribute("aria-label", "Stopp");
  stopBtn.title = "Stopp";
  stopBtn.innerHTML = STOP_ICON;

  const seek = document.createElement("input");
  seek.type = "range";
  seek.className = "player-bar-seek";
  seek.min = "0";
  seek.max = "1000";
  seek.value = "0";
  seek.step = "1";
  seek.setAttribute("aria-label", "Position");

  const timeEl = document.createElement("span");
  timeEl.className = "player-bar-time";
  const honestTitle =
    "Aufnahme abgerissen — soweit Audio da ist, abspielbar";
  const applyLabel = (cur: number, dur: number): void => {
    timeEl.textContent = playerTimeLabel(cur, dur, incomplete);
    if (incomplete && dur <= 0) timeEl.title = honestTitle;
    else timeEl.removeAttribute("title");
  };
  applyLabel(0, knownSec);

  /*
   * Die Zeit steht RECHTS IN DER KNOPFREIHE, nicht darunter. (05.09.2026)
   *
   * Martin, zum zweiten Mal: *„Ich habe gesagt, dass dieses Abspielfeld von
   * dem Player schrecklich aussieht und viel zu groß ist für ein Musikstück.
   * Kann man die Abspielelemente kompakter gestalten?"*
   *
   * Beim ersten Mal habe ich nur den VORLESE-Player vermessen und verkleinert
   * (142 → 104 Punkte) und dazu gesagt, der Musikplayer nutze dieselbe Leiste
   * und sei „vermutlich" mitgeschrumpft. Er nutzt dieselben Klassen, aber
   * eine andere Anordnung: Die Zeit hing als eigenes Kind hinter dem
   * Schieber und brach damit in eine dritte Zeile um.
   *
   * ⚠️ Derselbe Behälter `player-bar-uhr` wie dort — nicht eine zweite
   *    Lösung fürs selbe Problem. Hier trägt er sogar mehr: Der Musikplayer
   *    hat nur zwei Knöpfe, also liegt rechts noch mehr Platz brach.
   */
  const uhr = document.createElement("div");
  uhr.className = "player-bar-uhr";
  uhr.append(timeEl);
  /*
   * Herz und Sichern stehen RECHTS, hinter der Uhr — sie gehören zum Stück,
   * nicht zur Wiedergabe. Links bleibt die Bedienung (Abspielen, Stopp,
   * Position), und die ändert sich dadurch für keine der übrigen Stellen,
   * die diesen Player nutzen.
   */
  const zusatz: HTMLElement[] = [];
  if (opts?.merken) {
    const herz = document.createElement("button");
    herz.type = "button";
    herz.className = "player-bar-skip player-bar-herz";
    let an = opts.merken.an;
    const zeichne = (): void => {
      herz.dataset.an = an ? "ja" : "nein";
      herz.innerHTML = an ? HERZ_VOLL : HERZ_LEER;
      const wort = an ? "Aus den Lieblingsstücken nehmen" : "Zu den Lieblingsstücken";
      herz.setAttribute("aria-label", wort);
      herz.setAttribute("aria-pressed", an ? "true" : "false");
      herz.title = wort;
    };
    zeichne();
    herz.addEventListener("click", (ev) => {
      ev.stopPropagation();
      herz.disabled = true;
      void opts
        .merken!.umschalten(!an)
        .then((neu) => {
          an = neu;
          zeichne();
        })
        .catch(() => undefined)
        .finally(() => {
          herz.disabled = false;
        });
    });
    zusatz.push(herz);
  }
  if (opts?.sichern) {
    const sichern = document.createElement("button");
    sichern.type = "button";
    sichern.className = "player-bar-skip player-bar-sichern";
    sichern.innerHTML = SICHERN_ICON;
    sichern.setAttribute("aria-label", "Stück sichern oder teilen");
    sichern.title = "Sichern / Teilen";
    sichern.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const url = typeof source === "string" ? source : audio.src;
      if (!url) return;
      void import("./share.js").then(({ shareMedia }) =>
        shareMedia({
          url,
          title: opts.titel ?? "Klang des Tages",
          dateiname: opts.sichern!.dateiname,
        }).catch(() => undefined),
      );
    });
    zusatz.push(sichern);
  }
  bar.append(playBtn, stopBtn, seek, uhr, ...zusatz);
  wrap.appendChild(bar);

  let seeking = false;

  const effectiveDuration = (): number =>
    resolveMediaDuration(audio.duration, seekableEndOf(audio), knownSec);

  const updateTime = (): void => {
    const cur = audio.currentTime;
    const dur = effectiveDuration();
    const max = Number(seek.max) || 1000;
    if (!seeking) {
      seek.value = dur > 0 ? String(Math.round((cur / dur) * max)) : "0";
      // 2026-09-05 (Martin: „ein gutes Beispiel ist der WhatsApp-Player"):
      // Der gespielte Teil wird sichtbar. Der Wert steht ohnehin hier — ihn
      // als Anteil weiterzugeben kostet eine Zeile und spart eine zweite
      // Berechnung im CSS.
      seek.style.setProperty(
        "--gehoert",
        dur > 0 ? `${Math.min(100, Math.round((cur / dur) * 100))}%` : "0%",
      );
    }
    applyLabel(cur, dur);
  };

  const setPlaying = (playing: boolean): void => {
    if (playing) setzeKnopf(PAUSE_ICON, "Pause", "laeuft");
    else setzeKnopf(PLAY_ICON, "Weiter", "pausiert");
  };

  audio.addEventListener("loadedmetadata", () => {
    updateTime();
    // Die Dauer kam erst jetzt — der ruhende Knopf nennt sie nach.
    if (kompakt && playBtn.dataset.state === "bereit")
      setzeKnopf(PLAY_ICON, "Abspielen", "bereit");
  });
  audio.addEventListener("durationchange", () => updateTime());
  audio.addEventListener("loadeddata", () => updateTime());
  audio.addEventListener("timeupdate", () => updateTime());
  audio.addEventListener("play", () => setPlaying(true));
  audio.addEventListener("pause", () => setPlaying(false));
  audio.addEventListener("ended", () => {
    setPlaying(false);
    audio.currentTime = 0;
    seek.value = "0";
    seek.style.setProperty("--gehoert", "0%");
    applyLabel(0, effectiveDuration());
    setzeKnopf(PLAY_ICON, "Abspielen", "bereit");
  });

  // Toter/abgebrochener Stream → src verwerfen, damit der nächste Tipp eine
  // FRISCHE URL auflöst statt die tote erneut abzuspielen.
  audio.addEventListener("error", () => {
    if (typeof source === "function") audio.removeAttribute("src");
    resolving = false;
    seek.value = "0";
    applyLabel(0, knownSec);
    setzeKnopf(PLAY_ICON, "Erneut", "fehler");
  });

  stopBtn.addEventListener("click", () => {
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      /* src evtl. noch nicht geladen */
    }
    seek.value = "0";
    updateTime();
    setzeKnopf(PLAY_ICON, "Abspielen", "bereit");
  });

  playBtn.addEventListener("click", () => {
    if (!audio.paused) {
      audio.pause();
      return;
    }
    // 2026-07-15 (Martin): A2DP-Umschlag schon beim Antippen anstossen —
    // nicht erst wenn das Audio laeuft (sonst blecherner erster Moment).
    prewarmHiFi();
    // Lazy-Quelle: beim ersten Play erst auflösen.
    if (!audio.src && typeof source === "function") {
      if (resolving) return;
      resolving = true;
      /*
       * ⚠️ 2026-09-03 (Martin, mit Foto: „hier steht ewig Lade, aber es
       * passiert gar nichts"):
       *
       * Hier stand nur "Lade …" — ohne Zeitgrenze und ohne Weg zurück. Bleibt
       * das Versprechen offen (haengende Synthese, abgerissenes Netz), bleibt
       * `resolving` fuer immer wahr: jeder weitere Tipp kehrt in Zeile eins
       * still um, und die Anzeige steht bis zum Neuladen der Seite.
       *
       * Gemessen ist eine frische Synthese ~12 s (aus dem Zwischenspeicher
       * 4 ms). 45 s lassen also reichlich Luft und fangen trotzdem jeden
       * echten Haenger ab — mit einer Beschriftung, die zum Nochmal-Tippen
       * einlaedt statt stumm zu bleiben.
       */
      const ZEITGRENZE_MS = 45_000;
      let erledigt = false;
      const beenden = (text: string | null): void => {
        if (erledigt) return;
        erledigt = true;
        resolving = false;
        if (text !== null) timeEl.textContent = text;
      };
      const wecker = window.setTimeout(
        () => beenden("Zu lange — nochmal tippen"),
        ZEITGRENZE_MS,
      );
      timeEl.textContent = "Lade …";
      void source()
        .then((u) => {
          window.clearTimeout(wecker);
          if (erledigt) return; // Zeitgrenze war schneller
          beenden(null);
          audio.src = u;
          void audio.play().catch(() => {});
        })
        .catch(() => {
          window.clearTimeout(wecker);
          beenden("Fehler — nochmal tippen");
        });
      return;
    }
    void audio.play().catch(() => {
      /* Autoplay blockiert — UI bleibt im Pause-Zustand */
    });
  });

  seek.addEventListener("mousedown", () => {
    seeking = true;
  });
  seek.addEventListener("input", () => {
    const dur = effectiveDuration();
    if (dur > 0) {
      const max = Number(seek.max) || 1000;
      applyLabel((Number(seek.value) / max) * dur, dur);
    }
  });
  seek.addEventListener("change", () => {
    const dur = effectiveDuration();
    if (dur > 0) {
      const max = Number(seek.max) || 1000;
      try {
        audio.currentTime = (Number(seek.value) / max) * dur;
      } catch {
        /* iOS verweigert Seek hinter dem Puffer — Position bleibt */
      }
    }
    seeking = false;
  });

  return wrap;
}
